import React, { useContext, useEffect } from 'react'
import styled from 'styled-components'
import {
  Button,
  Modal as BaseModal,
  ModalInjectedProps,
  useForTablet,
  ModalContext,
} from '@sketch/components'
import { Image as ImageUnstyled } from '@sketch/modules-common'
import Separator from 'modules/shares/Inspector/Sidebar/components/Separator'
import magnifyingGlassIcon from '@sketch/icons/magnifying.glass.png'
import magnifyingGlassIcon2x from '@sketch/icons/magnifying.glass@2x.png'
import copyIcon from '@sketch/icons/copy.png'
import copyIcon2x from '@sketch/icons/copy@2x.png'
import rulerIcon from '@sketch/icons/ruler.png'
import rulerIcon2x from '@sketch/icons/ruler@2x.png'
import dowloadAssetsIcon from '@sketch/icons/arrow.down.circle.png'
import dowloadAssetsIcon2x from '@sketch/icons/arrow.down.circle@2x.png'
import selectLayer from '@sketch/icons/select-layer-video.gif'
import { useLocalStorage } from 'react-use'

const Modal = styled(BaseModal)`
  position: relative;
  display: grid;
  grid-template-rows: auto 1fr auto;
  max-height: 100%;

  > div {
    overflow: auto;
  }
`

const IntroductionModalHeader = styled(BaseModal.Header)`
  display: flex;
`

const IntroductionItemIcon = styled.img.attrs({
  width: '64px',
  height: '64px',
})`
  min-width: 64px;
  min-height: 64px;
  margin-right: 20px;
`

const IntroductionItemTitle = styled.h4`
  color: ${({ theme }) => theme.colors.foreground.secondary.A};
  font-size: 1rem;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  line-height: 1;
  margin: 0 0 8px 0;
`

const LayerExportGif = styled(ImageUnstyled)`
  padding-top: 16px;
  margin-bottom: 40px;
  height: 205px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const IntroSeparator = styled(Separator)`
  padding: 12px 0;
`

const IntroductionIcons = styled.div`
  background-color: ${({ theme }) => theme.colors.background.secondary.B};
  margin-top: 16px;
  padding: 28px 44px;
  display: flex;
  justify-content: space-between;
`

const ArtboardDetailInspectorIntroductionModal: React.FC<ModalInjectedProps> = ({
  hideModal,
}) => {
  return (
    <Modal onCancel={hideModal}>
      <IntroductionModalHeader>Did you know&hellip;</IntroductionModalHeader>
      <BaseModal.Body>
        For documents with overlapping layers, you can Control-click (or
        right-click) over the layer you want to select and choose it from the
        contextual menu that appears.
        <LayerExportGif
          src={selectLayer}
          width={425}
          alt="Right click on a layer to trigger the contextual menu"
        />
        <IntroSeparator />
        <IntroductionItemTitle>New around here?</IntroductionItemTitle>
        With the Web Inspector, you can select and measure between layers on
        Artboards, view layer attributes, copy values to your clipboard and
        download production-ready assets. And it’s free!
        <IntroductionIcons>
          <IntroductionItemIcon
            src={magnifyingGlassIcon2x}
            srcSet={`${magnifyingGlassIcon2x} 2x, ${magnifyingGlassIcon} 1x`}
            alt="Check layer attributes"
          />
          <IntroductionItemIcon
            src={rulerIcon2x}
            srcSet={`${rulerIcon2x} 2x, ${rulerIcon} 1x`}
            alt="Measure distance between layers"
          />
          <IntroductionItemIcon
            src={copyIcon2x}
            srcSet={`${copyIcon2x} 2x, ${copyIcon} 1x`}
            alt="Quickly copy attributes"
          />
          <IntroductionItemIcon
            src={copyIcon2x}
            srcSet={`${dowloadAssetsIcon2x} 2x, ${dowloadAssetsIcon} 1x`}
            alt="Download production-ready assets"
          />
        </IntroductionIcons>
      </BaseModal.Body>
      <BaseModal.Footer>
        <Button onClick={hideModal} variant="primary">
          Let’s Go
        </Button>
      </BaseModal.Footer>
    </Modal>
  )
}

function useShowIntroductionModal() {
  const isTabletAndBigger = useForTablet()
  const { showModal } = useContext(ModalContext)
  const [introductionSeen, setIntroductionSeen] = useLocalStorage(
    'inspector_introduction_seen',
    false
  )

  useEffect(() => {
    if (!introductionSeen && isTabletAndBigger) {
      // Because of how Tabs are composed (Tab and TabPanel being at the same
      // level), this side effect is triggered before the URL change that
      // happens when clicking a <Tab /> (in this case #inspector), using
      // `setTimeout` allows to delay this execution until next tick
      setTimeout(() => {
        showModal(ArtboardDetailInspectorIntroductionModal)
        setIntroductionSeen(true)
      }, 0)
    }
  }, [showModal, introductionSeen, setIntroductionSeen, isTabletAndBigger])
}

export { ArtboardDetailInspectorIntroductionModal, useShowIntroductionModal }
